import { Fragment, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router';

import {
  MARKETING_APP_ENTRY_HREF,
  type MarketingLinkItem,
  marketingPrimaryNavItems,
} from '../marketingContent';

import kuduLogoUrl from '@/images/kudu-logo-full.svg';
import styles from './MarketingNavBar.module.css';

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

/**
 * A page below the item's own, as a help article is below Help: the item reads
 * as current there too, while aria-current marks only the exact page (#175).
 */
function isBelowItem(item: MarketingLinkItem, pathname: string) {
  return pathname.startsWith(`${item.href}/`);
}

/**
 * A nav item with pages under it: the item stays a link to its own page, and
 * a separate button beside it opens a menu of the pages. The item reads as
 * current on any of those pages; aria-current marks only the exact page.
 */
function NavItemWithMenu({ item, pathname }: { item: MarketingLinkItem; pathname: string }) {
  const children = item.children ?? [];
  const childIsCurrent = children.some((child) => child.href === pathname);

  return (
    <div className={styles.navGroup}>
      <NavLink
        className={({ isActive }) =>
          classNames(styles.navLink, (isActive || childIsCurrent) && styles.navLinkActive)
        }
        end
        to={item.href}
      >
        {item.label}
      </NavLink>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger aria-label={`More in ${item.label}`} className={styles.menuButton}>
          <ChevronDown aria-hidden="true" size={16} />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content align="start" className={styles.menuContent} sideOffset={6}>
            {children.map((child) => (
              <DropdownMenu.Item asChild key={child.href}>
                <Link
                  aria-current={child.href === pathname ? 'page' : undefined}
                  className={styles.menuItem}
                  to={child.href}
                >
                  <span className={styles.menuItemLabel}>
                    {child.label}
                    {child.badge ? <span className={styles.menuBadge}>{child.badge}</span> : null}
                  </span>
                  {child.description ? (
                    <span className={styles.menuItemDescription}>{child.description}</span>
                  ) : null}
                </Link>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

export function MarketingNavBar() {
  const { pathname } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header
      className={styles.header}
      data-scrolled={isScrolled ? 'true' : 'false'}
    >
      <div className={styles.inner}>
        <Link className={styles.brand} to="/">
          <img alt="Kudu" className={styles.brandLogo} src={kuduLogoUrl} />
        </Link>

        <nav aria-label="Marketing" className={styles.desktopNav}>
          {marketingPrimaryNavItems.map((item) =>
            item.children ? (
              <NavItemWithMenu item={item} key={item.href} pathname={pathname} />
            ) : (
              <NavLink
                className={({ isActive }) =>
                  classNames(
                    styles.navLink,
                    (isActive || isBelowItem(item, pathname)) && styles.navLinkActive,
                  )
                }
                end
                key={item.href}
                to={item.href}
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className={styles.rightRail}>
          <Link className={styles.loginLink} to={MARKETING_APP_ENTRY_HREF}>
            Log In
          </Link>

          <Dialog.Root onOpenChange={setIsMenuOpen} open={isMenuOpen}>
            <Dialog.Trigger asChild>
              <button
                aria-label="Open navigation menu"
                className={styles.menuTrigger}
                type="button"
              >
                <Menu aria-hidden="true" size={20} />
              </button>
            </Dialog.Trigger>

            <Dialog.Portal>
              <Dialog.Overlay className={styles.mobileOverlay} />
              <Dialog.Content className={styles.mobileContent}>
                <Dialog.Title className={styles.srOnly}>
                  Navigation menu
                </Dialog.Title>
                <Dialog.Description className={styles.srOnly}>
                  Browse the site&apos;s pages, or log in.
                </Dialog.Description>

                <div className={styles.mobileHeader}>
                  <Link
                    className={styles.brand}
                    onClick={() => setIsMenuOpen(false)}
                    to="/"
                  >
                    <img
                      alt="Kudu"
                      className={styles.brandLogo}
                      src={kuduLogoUrl}
                    />
                  </Link>

                  <Dialog.Close asChild>
                    <button
                      aria-label="Close navigation menu"
                      className={styles.closeButton}
                      type="button"
                    >
                      <X aria-hidden="true" size={20} />
                    </button>
                  </Dialog.Close>
                </div>

                <nav aria-label="Mobile marketing" className={styles.mobileNav}>
                  {marketingPrimaryNavItems.map((item) => (
                    <Fragment key={item.href}>
                      <NavLink
                        className={({ isActive }) =>
                          classNames(
                            styles.mobileNavLink,
                            (isActive || isBelowItem(item, pathname)) &&
                              styles.mobileNavLinkActive,
                          )
                        }
                        end
                        onClick={() => setIsMenuOpen(false)}
                        to={item.href}
                      >
                        {item.label}
                      </NavLink>
                      {/* Pages under the item, except its own (the link above). */}
                      {(item.children ?? [])
                        .filter((child) => child.href !== item.href)
                        .map((child) => (
                          <NavLink
                            className={({ isActive }) =>
                              classNames(
                                styles.mobileNavLink,
                                styles.mobileSubLink,
                                isActive && styles.mobileNavLinkActive,
                              )
                            }
                            end
                            key={child.href}
                            onClick={() => setIsMenuOpen(false)}
                            to={child.href}
                          >
                            {child.label}
                            {child.badge ? (
                              <span className={styles.menuBadge}>{child.badge}</span>
                            ) : null}
                          </NavLink>
                        ))}
                    </Fragment>
                  ))}
                </nav>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      </div>
    </header>
  );
}
